import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  AlertCircle,
  Check,
  Eye,
  History,
  Link as LinkIcon,
  RefreshCw,
  Save,
  Send,
  Undo2,
  X as XIcon,
} from 'lucide-react';
import {
  type CategoryRef,
  type CreatePostInput,
  type PostDetail,
  type SessionUser,
  type TagRef,
  createPostSchema,
} from '@news-cms/shared';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { PostPreviewDialog } from './post-preview-dialog';
import { PostStatusBadge } from './post-status-badge';
import { PreviewShareDialog } from './preview-share-dialog';
import { PublishDialog } from './publish-dialog';
import { RevisionsDialog } from './revisions-dialog';
import { TaxonomyPicker } from './taxonomy-picker';
import { CoverImagePicker } from './cover-image-picker';
import { SeoScorePanel } from './seo-score-panel';
import { slugifyForPreview } from '@/lib/slugify';
import { htmlFromBlocks } from '@/lib/editor-blocks';
import { datetimePickerHandlers } from '@/lib/datetime-picker';
import { hasPermission } from '@/lib/permissions';
import { postErrorMessage, useSlugCheck } from '@/lib/posts';

// Lazy-load the rich editor so the TipTap bundle (~700kB) only loads on
// edit/new post pages, not on list/login/dashboard.
const PostEditor = lazy(() => import('./post-editor').then((m) => ({ default: m.PostEditor })));

const EXCERPT_MAX_LEN = 160;

/**
 * Build a meta-description-friendly summary from the raw editor HTML.
 * Strips tags, decodes the most-common entities, collapses whitespace,
 * then takes the first ~160 chars — prefers ending at a sentence
 * boundary (period, Thai full-stop, question, exclamation, newline)
 * when one falls inside the last 60 chars of the window, otherwise
 * trims at a word boundary and adds ellipsis.
 *
 * Mirrors server-side stripHtmlTags (lib/post-derive.ts) loosely;
 * runs client-side so admin sees instant preview without round-trip.
 */
function excerptFromBody(html: string): string {
  if (!html) return '';
  const text = html
    .replace(/<\/(p|h[1-6]|li|blockquote|pre|br)>/gi, ' ')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
  if (!text) return '';
  if (text.length <= EXCERPT_MAX_LEN) return text;
  const window = text.slice(0, EXCERPT_MAX_LEN);
  // Prefer a sentence break in the last 60 chars — keeps the excerpt
  // from cutting mid-thought when one's available.
  const sentenceBreak = Math.max(
    window.lastIndexOf('. '),
    window.lastIndexOf('? '),
    window.lastIndexOf('! '),
    window.lastIndexOf('ฯ '),
  );
  if (sentenceBreak >= EXCERPT_MAX_LEN - 60) {
    return window.slice(0, sentenceBreak + 1).trim();
  }
  const wordBreak = window.lastIndexOf(' ');
  return (wordBreak > 0 ? window.slice(0, wordBreak) : window).trim() + '...';
}

/**
 * ISO 8601 (UTC) → `YYYY-MM-DDTHH:mm` ใน LOCAL timezone สำหรับ
 * <input type="datetime-local">. คืน '' ถ้า null/undefined.
 *
 * เหตุที่ทำเองแทน toISOString().slice(0,16): ค่านั้นเป็น UTC ไม่ใช่ local —
 * แสดงผิดเวลาให้ user. เราต้อง shift ด้วย timezone offset แทน.
 */
function isoToLocalDateTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  // adjust by tz offset (offset เป็น minutes, getTimezoneOffset() คืน
  // ค่าบวกสำหรับ tz ที่ตามหลัง UTC — สำหรับไทย = -420)
  const tzOffsetMs = d.getTimezoneOffset() * 60_000;
  const local = new Date(d.getTime() - tzOffsetMs);
  return local.toISOString().slice(0, 16);
}

/**
 * `YYYY-MM-DDTHH:mm` ใน LOCAL timezone → ISO 8601 UTC. คืน null ถ้า empty
 * หรือ invalid (เก็บ field เป็น null ใน DB ถ้า user ลบเวลาออก).
 */
function localDateTimeToIso(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function EditorFallback() {
  return (
    <div className="border-input bg-background flex h-[400px] items-center justify-center rounded-md border text-sm text-slate-500">
      กำลังโหลด editor...
    </div>
  );
}

export interface PostFormProps {
  mode: 'create' | 'edit';
  initial?: PostDetail;
  user: SessionUser | null;
  onSubmit: (data: CreatePostInput) => Promise<void>;
  onPublish?: (scheduledAt?: string) => void;
  onUnpublish?: () => void;
  isSubmitting: boolean;
  isPublishing?: boolean;
  isUnpublishing?: boolean;
  error?: unknown;
  publishError?: unknown;
}

export function PostForm({
  mode,
  initial,
  user,
  onSubmit,
  onPublish,
  onUnpublish,
  isSubmitting,
  isPublishing,
  isUnpublishing,
  error,
  publishError,
}: PostFormProps) {
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [revisionsOpen, setRevisionsOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  // Mobile-only single-column toggle. On `lg:` and up both panes render.
  const [mobileTab, setMobileTab] = useState<'content' | 'meta'>('content');
  const [selectedCategories, setSelectedCategories] = useState<CategoryRef[]>(
    initial?.categories ?? [],
  );
  const [selectedTags, setSelectedTags] = useState<TagRef[]>(initial?.tags ?? []);
  const [coverImage, setCoverImage] = useState<{
    id: string;
    url: string;
    altText: string | null;
  } | null>(
    initial?.featuredImage
      ? {
          id: initial.featuredImage.id,
          url: initial.featuredImage.url,
          altText: initial.featuredImage.altText,
        }
      : null,
  );

  // Editor initial: convert stored PostBlock[] → HTML (handles both html-type
  // and legacy paragraph-type blocks). Empty for create.
  const initialBodyHtml = htmlFromBlocks(initial?.body);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<CreatePostInput>({
    resolver: zodResolver(createPostSchema),
    defaultValues: initial
      ? {
          title: initial.title,
          slug: initial.slug,
          summary: initial.summary ?? '',
          body: initialBodyHtml,
          language: initial.language,
          visibility: initial.visibility,
          isSticky: initial.isSticky,
          isBreaking: initial.isBreaking,
          isFeatured: initial.isFeatured,
          isPressRelease: initial.isPressRelease,
          expiryAt: initial.expiryAt,
          allowComments: initial.allowComments,
          metaTitle: initial.metaTitle ?? '',
          metaDescription: initial.metaDescription ?? '',
          focusKeyword: initial.focusKeyword ?? '',
        }
      : {
          title: '',
          slug: '',
          body: '',
          language: 'th',
          visibility: 'public',
          isSticky: false,
          isBreaking: false,
          isFeatured: false,
          isPressRelease: false,
          expiryAt: null,
          allowComments: true,
        },
  });

  const watchedTitle = watch('title');
  const watchedSlug = watch('slug');

  // Auto-bump slug from title only if user hasn't manually edited it
  const handleRegenerateSlug = () => {
    setValue('slug', slugifyForPreview(watchedTitle || ''));
    setSlugManuallyEdited(false);
  };

  // Debounce the slug value for the live availability check so we
  // don't hammer the endpoint on every keystroke. 400ms = typical
  // pause between words while typing.
  const [debouncedSlug, setDebouncedSlug] = useState(watchedSlug ?? '');
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSlug(watchedSlug ?? ''), 400);
    return () => clearTimeout(id);
  }, [watchedSlug]);

  // Skip the check entirely when editing and the slug equals the saved
  // value — that's "this slug is mine, of course it's taken" noise.
  const slugIsUnchanged = mode === 'edit' && debouncedSlug === (initial?.slug ?? '');
  const slugCheck = useSlugCheck(
    slugIsUnchanged ? '' : debouncedSlug.trim(),
    mode === 'edit' ? initial?.id : undefined,
  );

  const canPublish = hasPermission(user, 'post.publish');
  const canUnpublish = hasPermission(user, 'post.unpublish');
  const canManageCategory = hasPermission(user, 'category.manage');
  const canManageTag = hasPermission(user, 'tag.manage');
  const isPublished = initial?.status === 'published';
  const isScheduled = initial?.status === 'scheduled';

  // Track the wall-clock time of the most recent successful save so
  // we can render a "บันทึกล่าสุด HH:MM" indicator. Live time (Date.now)
  // is fine — sub-second precision isn't needed; the value is just to
  // reassure the author that the worker round-trip completed.
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const submitInner = async (data: CreatePostInput) => {
    await onSubmit({
      ...data,
      categoryIds: selectedCategories.map((c) => c.id),
      tagIds: selectedTags.map((t) => t.id),
      featuredImageId: coverImage?.id ?? null,
    });
    setLastSavedAt(Date.now());
  };

  // Ctrl+S / Cmd+S → save (same as clicking the "บันทึก" button).
  // Bound on window so it works from anywhere in the form, including
  // the TipTap editor where StarterKit's own shortcut map doesn't
  // intercept the combo. Skip while a save is already in flight so
  // rapid Ctrl+S spam can't queue duplicate submits.
  const formRef = useRef<HTMLFormElement | null>(null);
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        if (isSubmitting) return;
        // requestSubmit fires the form's onSubmit which routes through
        // RHF + submitInner, so validation + categories/tags merge run.
        formRef.current?.requestSubmit();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isSubmitting]);

  return (
    <form
      ref={formRef}
      onSubmit={(e) => {
        void handleSubmit(submitInner)(e);
      }}
      className="space-y-4 lg:grid lg:grid-cols-3 lg:gap-6 lg:space-y-0"
      noValidate
    >
      {/* Mobile-only segmented toggle: เนื้อหา | ตั้งค่า */}
      <div className="flex gap-1 rounded-lg border border-slate-200 bg-white p-1 lg:hidden dark:border-slate-800 dark:bg-slate-900">
        <button
          type="button"
          onClick={() => setMobileTab('content')}
          className={
            mobileTab === 'content'
              ? 'bg-brand-600 flex-1 rounded px-3 py-1.5 text-xs font-medium text-white'
              : 'flex-1 rounded px-3 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300'
          }
        >
          เนื้อหา
        </button>
        <button
          type="button"
          onClick={() => setMobileTab('meta')}
          className={
            mobileTab === 'meta'
              ? 'bg-brand-600 flex-1 rounded px-3 py-1.5 text-xs font-medium text-white'
              : 'flex-1 rounded px-3 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300'
          }
        >
          ตั้งค่า
        </button>
      </div>

      {/* Main column */}
      <div
        className={`space-y-4 lg:col-span-2 lg:block ${mobileTab === 'content' ? 'block' : 'hidden'}`}
      >
        {Boolean(error ?? publishError) && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{postErrorMessage(error ?? publishError)}</AlertDescription>
          </Alert>
        )}

        <Card>
          <CardContent className="space-y-4 pt-6">
            <div>
              <Label htmlFor="title" className="mb-1.5 block">
                หัวข้อ *
              </Label>
              <Input
                id="title"
                {...register('title', {
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                    if (!slugManuallyEdited && mode === 'create') {
                      setValue('slug', slugifyForPreview(e.target.value));
                    }
                  },
                })}
                placeholder="หัวข้อบทความ"
              />
              {errors.title && (
                <p className="text-destructive mt-1 text-xs">{errors.title.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="slug" className="mb-1.5 block">
                Slug
              </Label>
              <div className="flex gap-2">
                <Input
                  id="slug"
                  {...register('slug', {
                    onChange: () => setSlugManuallyEdited(true),
                  })}
                  placeholder="my-post-slug"
                  className="font-mono"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={handleRegenerateSlug}
                  aria-label="Regenerate slug from title"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
              {errors.slug && (
                <p className="text-destructive mt-1 text-xs">{errors.slug.message}</p>
              )}
              <p className="mt-1 text-[11px] text-slate-500">URL: /{watchedSlug || '(auto)'}</p>
              {/* Live availability hint — debounced 400ms behind keystrokes,
                  skipped entirely when the slug equals the saved value on
                  an edit page (it's "of course taken — by this post"). */}
              {!slugIsUnchanged && debouncedSlug.trim().length > 0 && slugCheck.data && (
                <p
                  className={`mt-1 inline-flex items-center gap-1 text-[11px] ${
                    slugCheck.data.valid && slugCheck.data.available
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-destructive'
                  }`}
                >
                  {slugCheck.data.valid && slugCheck.data.available ? (
                    <>
                      <Check className="h-3 w-3" /> ใช้ slug นี้ได้
                    </>
                  ) : slugCheck.data.reason === 'invalid' ? (
                    <>
                      <XIcon className="h-3 w-3" /> slug ใช้ไม่ได้ — ตัวพิมพ์เล็ก/ไทย/ตัวเลข +
                      ขีดกลาง
                    </>
                  ) : (
                    <>
                      <XIcon className="h-3 w-3" /> slug นี้ถูกใช้แล้ว
                    </>
                  )}
                </p>
              )}
            </div>

            <div>
              <div className="mb-1.5 flex items-center justify-between gap-2">
                <Label htmlFor="summary">สรุปสั้น (excerpt)</Label>
                <button
                  type="button"
                  onClick={() => {
                    const body = watch('body') ?? '';
                    const generated = excerptFromBody(body);
                    if (generated) {
                      setValue('summary', generated, { shouldDirty: true });
                    }
                  }}
                  className="text-brand-600 hover:text-brand-700 dark:text-brand-400 text-xs hover:underline"
                >
                  สร้างจากเนื้อหา
                </button>
              </div>
              <Textarea
                id="summary"
                {...register('summary')}
                placeholder="สรุปข่าวสั้นๆ สำหรับ list และ SEO"
                rows={2}
              />
            </div>

            <div>
              <Label className="mb-1.5 block">เนื้อหา</Label>
              {/* Register body so RHF tracks/validates it; PostEditor pushes
                  HTML into the form via setValue on change */}
              <input type="hidden" {...register('body')} />
              <Suspense fallback={<EditorFallback />}>
                <PostEditor
                  initialHtml={initialBodyHtml}
                  onChange={(html) => setValue('body', html, { shouldDirty: true })}
                />
              </Suspense>
              {errors.body && (
                <p className="text-destructive mt-1 text-xs">{errors.body.message}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* SEO card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">SEO / Social</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="mb-1.5 block">รูปปก (Cover image)</Label>
              <CoverImagePicker selected={coverImage} onChange={setCoverImage} />
            </div>

            <div>
              <Label htmlFor="metaTitle" className="mb-1.5 block">
                Meta title
              </Label>
              <Input
                id="metaTitle"
                {...register('metaTitle')}
                placeholder={watchedTitle || 'ใช้หัวข้อบทความถ้าเว้นว่าง'}
                maxLength={60}
              />
              <p className="mt-1 text-[11px] text-slate-500">
                {watch('metaTitle')?.length ?? 0}/60 ตัวอักษร — แนะนำไม่เกิน 60
              </p>
            </div>

            <div>
              <Label htmlFor="metaDescription" className="mb-1.5 block">
                Meta description
              </Label>
              <Textarea
                id="metaDescription"
                {...register('metaDescription')}
                placeholder="คำอธิบายสำหรับ Google + social media preview"
                rows={3}
                maxLength={160}
              />
              <p className="mt-1 text-[11px] text-slate-500">
                {watch('metaDescription')?.length ?? 0}/160 ตัวอักษร — แนะนำ 150-160
              </p>
            </div>

            <div>
              <Label htmlFor="focusKeyword" className="mb-1.5 block">
                Focus keyword
              </Label>
              <Input
                id="focusKeyword"
                {...register('focusKeyword')}
                placeholder="คำสำคัญหลักของบทความ"
              />
            </div>

            {/* Google snippet preview */}
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-900">
              <p className="mb-2 text-[11px] uppercase text-slate-400">Google preview</p>
              <div className="space-y-0.5">
                <div className="line-clamp-1 text-[13px] text-emerald-700 dark:text-emerald-400">
                  yoursite.com › {watchedSlug || '(slug)'}
                </div>
                <div className="line-clamp-1 text-[18px] text-blue-700 hover:underline dark:text-blue-400">
                  {watch('metaTitle') || watchedTitle || 'หัวข้อบทความ'}
                </div>
                <div className="line-clamp-2 text-[13px] text-slate-700 dark:text-slate-300">
                  {watch('metaDescription') || watch('summary') || 'คำอธิบายของบทความจะแสดงตรงนี้'}
                </div>
              </div>
            </div>

            <SeoScorePanel
              title={watchedTitle ?? ''}
              metaTitle={watch('metaTitle') ?? ''}
              metaDescription={watch('metaDescription') ?? ''}
              focusKeyword={watch('focusKeyword') ?? ''}
              bodyHtml={watch('body') ?? ''}
              hasCoverImage={coverImage !== null}
            />
          </CardContent>
        </Card>
      </div>

      {/* Side column */}
      <div className={`space-y-4 lg:block ${mobileTab === 'meta' ? 'block' : 'hidden'}`}>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">สถานะ</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {initial && (
              <div>
                <PostStatusBadge status={initial.status} />
              </div>
            )}
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full"
              size="sm"
              title="Ctrl+S"
            >
              <Save className="mr-2 h-4 w-4" />
              {isSubmitting ? 'กำลังบันทึก...' : 'บันทึก'}
              {!isSubmitting && (
                <kbd className="ml-auto rounded border border-white/30 bg-white/10 px-1 font-mono text-[9px]">
                  ⌘S
                </kbd>
              )}
            </Button>
            {lastSavedAt !== null && !isSubmitting && (
              <p className="text-center text-[10px] text-slate-500 dark:text-slate-400">
                บันทึกล่าสุด{' '}
                {new Date(lastSavedAt).toLocaleTimeString('th-TH', {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                })}
              </p>
            )}

            {mode === 'edit' && initial && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => setPreviewOpen(true)}
              >
                <Eye className="mr-2 h-4 w-4" />
                พรีวิว
              </Button>
            )}

            {mode === 'edit' && initial && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => setShareOpen(true)}
              >
                <LinkIcon className="mr-2 h-4 w-4" />
                แชร์ลิงก์พรีวิว
              </Button>
            )}

            {mode === 'edit' && initial && canPublish && !isPublished && (
              <Button
                type="button"
                variant="default"
                disabled={isPublishing}
                onClick={() => setPublishOpen(true)}
                className="w-full"
                size="sm"
              >
                <Send className="mr-2 h-4 w-4" />
                {isPublishing
                  ? 'กำลังเผยแพร่...'
                  : isScheduled
                    ? 'เปลี่ยนเวลา / เผยแพร่'
                    : 'เผยแพร่'}
              </Button>
            )}

            {mode === 'edit' && initial && isScheduled && initial.scheduledAt && (
              <p className="text-[11px] text-amber-600 dark:text-amber-400">
                ตั้งเวลาเผยแพร่ {new Date(initial.scheduledAt).toLocaleString('th-TH')}
              </p>
            )}

            {mode === 'edit' && initial && canUnpublish && (isPublished || isScheduled) && (
              <Button
                type="button"
                variant="outline"
                disabled={isUnpublishing}
                onClick={onUnpublish}
                className="w-full"
                size="sm"
              >
                <Undo2 className="mr-2 h-4 w-4" />
                {isUnpublishing
                  ? 'กำลังถอน...'
                  : isScheduled
                    ? 'ยกเลิกการตั้งเวลา'
                    : 'ถอนการเผยแพร่'}
              </Button>
            )}

            {mode === 'edit' && initial && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => setRevisionsOpen(true)}
                className="w-full"
                size="sm"
              >
                <History className="mr-2 h-4 w-4" />
                ประวัติการแก้ไข
              </Button>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">หมวด / แท็ก</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <TaxonomyPicker
              kind="category"
              label="หมวดหมู่"
              selected={selectedCategories}
              onChange={(next) => setSelectedCategories(next)}
              canCreate={canManageCategory}
            />
            <TaxonomyPicker
              kind="tag"
              label="แท็ก"
              selected={selectedTags}
              onChange={(next) => setSelectedTags(next)}
              canCreate={canManageTag}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">การตั้งค่า</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label htmlFor="language" className="mb-1.5 block">
                ภาษา
              </Label>
              <select
                id="language"
                {...register('language')}
                className="border-input bg-background flex h-10 w-full rounded-md border px-3 text-sm"
              >
                <option value="th">ไทย</option>
                <option value="en">English</option>
              </select>
            </div>

            <div className="space-y-2 pt-2">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" {...register('isSticky')} className="h-4 w-4" />
                ปักหมุด (Sticky)
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" {...register('isBreaking')} className="h-4 w-4" />
                Breaking News
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" {...register('isFeatured')} className="h-4 w-4" />
                ข่าวเด่น (Featured)
              </label>
              {/* ข่าวประชาสัมพันธ์ — auto-archive ผ่าน cron 5 นาทีเมื่อถึงวัน
                  หมดอายุ. เมื่อถอนติ๊ก expiryAt จะถูกเคลียร์ที่ backend
                  (เพื่อไม่ให้โพสต์ปกติมีเวลาหมดอายุค้าง). */}
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  {...register('isPressRelease', {
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                      // ถ้าถอนติ๊ก → เคลียร์ expiryAt ใน form state ให้สอดคล้อง
                      // กับสิ่งที่ backend จะทำ (ป้องกัน refine() validation error)
                      if (!e.target.checked) {
                        setValue('expiryAt', null, { shouldDirty: true, shouldValidate: true });
                      }
                    },
                  })}
                  className="h-4 w-4"
                />
                ข่าวประชาสัมพันธ์
              </label>
              {watch('isPressRelease') && (
                <div className="ml-6 space-y-1">
                  <Label
                    htmlFor="expiryAt"
                    className="text-[11px] text-slate-600 dark:text-slate-400"
                  >
                    วันหมดอายุ *
                  </Label>
                  <Input
                    id="expiryAt"
                    type="datetime-local"
                    value={isoToLocalDateTime(watch('expiryAt'))}
                    onChange={(e) => {
                      const next = localDateTimeToIso(e.target.value);
                      setValue('expiryAt', next, { shouldDirty: true, shouldValidate: true });
                    }}
                    {...datetimePickerHandlers}
                    className="h-10 cursor-pointer text-sm"
                  />
                  {errors.expiryAt && (
                    <p className="text-destructive text-[11px]">{errors.expiryAt.message}</p>
                  )}
                  <p className="text-[10px] text-slate-500 dark:text-slate-400">
                    ถึงเวลาแล้วระบบจะย้ายไปยังสถานะ "archived" อัตโนมัติ (ตรวจทุก 5 นาที)
                  </p>
                </div>
              )}
              {/* Comment system disabled site-wide per BRIEF.1 — checkbox hidden;
                  defaults keep `allowComments=true` so the field stays valid in
                  case comments are re-enabled later. */}
            </div>
          </CardContent>
        </Card>
      </div>

      {mode === 'edit' && initial && (
        <>
          <PostPreviewDialog
            open={previewOpen}
            onOpenChange={setPreviewOpen}
            draft={{
              title: watchedTitle ?? '',
              summary: watch('summary') ?? null,
              body: watch('body') ?? '',
              metaTitle: watch('metaTitle') ?? null,
              metaDescription: watch('metaDescription') ?? null,
            }}
            saved={initial}
          />
          <PreviewShareDialog open={shareOpen} onOpenChange={setShareOpen} postId={initial.id} />
          <RevisionsDialog
            open={revisionsOpen}
            onOpenChange={setRevisionsOpen}
            postId={initial.id}
          />
          <PublishDialog
            open={publishOpen}
            onOpenChange={setPublishOpen}
            isLoading={isPublishing}
            onConfirm={(scheduledAt) => {
              setPublishOpen(false);
              onPublish?.(scheduledAt);
            }}
          />
        </>
      )}
    </form>
  );
}
